/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useRef, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Navigation from "./components/Navigation";
import HeroSection from "./components/sections/HeroSection";
import StorySection from "./components/sections/StorySection";
import WhyFijoliSection from "./components/sections/WhyFijoliSection";
import WhatsInBookSection from "./components/sections/WhatsInBookSection";
import WhoShouldReadFijoli from "./components/sections/WhoShouldReadFijoli";
import CTASection from "./components/sections/CTASection";
import Footer from "./components/Footer";
import OnlineBookAccess from "./components/book/OnlineBookAccess";
import AccessOptions from "./components/access/AccessOptions";
import AdminDashboard from "./components/admin/AdminDashboard";
import ResponsiveReader from "./components/book/ResponsiveReader";
import LoginModal from "./components/auth/LoginModal";
import PaymentPage from "./components/payment/PaymentPage";
import LegalPage from "./components/LegalPage";
// WhatsApp icon import removed as it's not being used
// import { FaWhatsapp } from 'react-icons/fa';

// Home Page Component
const HomePage = () => {
  const navigate = useNavigate();
  const { user, hasPurchased } = useAuth();
  const sectionsRef = useRef([]);
  const [showAccessOptions, setShowAccessOptions] = useState(false);
  const [showOnlineBook, setShowOnlineBook] = useState(false);
  const [showAdminDashboard, setShowAdminDashboard] = useState(false);
  const [showLogin, setShowLogin] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      {
        threshold: 0.1,
      }
    );

    const currentSections = sectionsRef.current;
    currentSections.forEach((section) => {
      if (section) {
        observer.observe(section);
      }
    });

    return () => observer.disconnect();
  }, []);



  const setSectionRef = (element, index) => {
    sectionsRef.current[index] = element;
  };

  const handleOpenAccessBook = () => {
    setShowAccessOptions(true);
  };

  const handleCloseAccessBook = () => {
    setShowAccessOptions(false);
  };

  const handleOpenOnlineBook = () => {
    setShowOnlineBook(true);
  };

  const handleCloseOnlineBook = () => {
    setShowOnlineBook(false);
  };

  const handleOpenAdminDashboard = () => {
    setShowAdminDashboard(true);
  };

  const handleCloseAdminDashboard = () => {
    setShowAdminDashboard(false);
  };

  const handleOpenLogin = () => {
    setShowLogin(true);
  };

  const handleCloseLogin = () => {
    setShowLogin(false);
  };

  const handleAccessGranted = () => {
    // Close access options and navigate to page flip reader route
    setShowAccessOptions(false);
    navigate('/reader-pageflip');
  };

  return (
    <div className="bg-white">
      <Navigation 
        onOpenAccessBook={handleOpenAccessBook}
        onOpenOnlineBook={handleOpenOnlineBook}
        onOpenAdminDashboard={handleOpenAdminDashboard}
        onOpenLogin={handleOpenLogin}
      />
      
      <HeroSection setSectionRef={setSectionRef} index={0} />
      <StorySection setSectionRef={setSectionRef} index={1} />
      <WhyFijoliSection setSectionRef={setSectionRef} index={2} />
      <WhatsInBookSection setSectionRef={setSectionRef} index={3} />
      <WhoShouldReadFijoli setSectionRef={setSectionRef} index={4} />
      <CTASection setSectionRef={setSectionRef} index={5} />
      <Footer />

      {/* Floating Action Buttons */}
      <div className="fixed bottom-8 right-8 flex flex-col items-end gap-3 z-50">
        {/* Mobile Access Book Button - WhatsApp Style FAB */}
        <div className="lg:hidden flex flex-col items-end gap-2">
          {/* Access Book FAB with Blinking Text */}
          <div className="flex flex-col items-center gap-2">
            {/* Blinking Early Bird Offer Text - Only for non-purchased users */}
            {!user || !hasPurchased ? (
              <div className="bg-red-600 text-white px-3 py-2 rounded-lg text-xs font-semibold animate-highlight-blink text-center shadow-lg">
                <div className="animate-blink">Avail Early Bird offer</div>
              </div>
            ) : null}
            
            {/* Book FAB Button with Text */}
            <button
              onClick={() => {
                if (!user) {
                  // User is not logged in, show login modal directly
                  handleOpenLogin();
                } else if (hasPurchased) {
                  // User is logged in and has paid, navigate directly to book
                  navigate('/reader-pageflip');
                } else {
                  // User is logged in but hasn't paid, show access options
                  handleOpenAccessBook();
                }
              }}
              className="bg-red-600 text-white px-4 py-3 rounded-full shadow-lg hover:bg-red-700 transition-all duration-300 hover:shadow-xl transform hover:scale-110 font-semibold flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
              </svg>
              <span className="text-sm font-semibold">
                {user && hasPurchased ? 'Read Book' : 'Access Book'}
              </span>
            </button>
          </div>
        </div>

        {/* WhatsApp Support Button */}
        <div className="flex flex-row items-end gap-2 items-center">
          <div className="bg-red-600 text-white px-3 py-2 rounded-lg text-xs font-semibold text-center">
            Support | Ask us on New Topics
          </div>
          <a
            href="https://wa.me/+919008023230"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-green-500 text-white p-4 rounded-full shadow-lg hover:bg-green-600 transition-colors hover-scale"
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
            </svg>
          </a>
        </div>
      </div>

      {/* Login Modal */}
      {showLogin && (
        <LoginModal
          isOpen={showLogin}
          onClose={handleCloseLogin}
        />
      )}

      {/* Access Options Modal */}
      {showAccessOptions && (
        <AccessOptions
          isOpen={showAccessOptions}
          onClose={handleCloseAccessBook}
          onAccessGranted={handleAccessGranted}
        />
      )}

      {/* Online Book Access Modal */}
      {showOnlineBook && (
        <OnlineBookAccess
          onClose={handleCloseOnlineBook}
          onBackToWebsite={handleCloseOnlineBook}
        />
      )}

      {/* Admin Dashboard Modal */}
      {showAdminDashboard && (
        <AdminDashboard
          isOpen={showAdminDashboard}
          onClose={handleCloseAdminDashboard}
        />
      )}

    </div>
  );
};

// This function is not used and causes a duplicate declaration error
// Removing it as the main App component is defined below

/* function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/legal" element={<LegalPage />} />
        <Route path="/reader-pageflip" element={<ResponsiveReader usePageFlip={true} />} />
        <Route path="/payment" element={<PaymentPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
} */

function AppContent() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/legal" element={<LegalPage />} />
        <Route path="/reader-pageflip" element={<ResponsiveReader usePageFlip={true} />} />
        <Route path="/payment" element={<PaymentPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

const App = React.memo(() => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
});

export default App;